import { useState } from 'react';
import type { ControlCommand } from '@karozik/shared';
import { cn } from './api.js';
import type { PartyConnection } from './useParty.js';

type Send = (c: ControlCommand) => Promise<void>;

function useControl(conn: PartyConnection) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send: Send = async (c) => {
    setBusy(true);
    const r = await conn.send('control', c);
    setBusy(false);
    setError(r.ok ? null : (r.error ?? 'Erreur'));
  };
  return { send, error, busy };
}

function Tile({ emoji, label, onClick, active, disabled }: { emoji: string; label: string; onClick(): void; active?: boolean; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center gap-1 rounded-2xl px-2 py-4 text-sm font-semibold transition active:scale-95 disabled:opacity-40',
        active ? 'bg-gradient-neon text-white shadow-lg shadow-pink/30' : 'bg-surface-2',
      )}
    >
      <span className="text-3xl">{emoji}</span>
      {label}
    </button>
  );
}

/**
 * Organiser shortcuts shared by the admin remote and the kiosk animator mode:
 * microphone, moments, blind test, phase, skip.
 */
export function MomentControls({ conn, showSkip = true }: { conn: PartyConnection; showSkip?: boolean }) {
  const { send, error, busy } = useControl(conn);
  const [prompt, setPrompt] = useState<null | 'birthday' | 'announce' | 'mic'>(null);
  const [text, setText] = useState('');
  const state = conn.state;
  if (!state) return null;
  const bt = state.blindtest;
  const faces = state.faces;

  const submitPrompt = async () => {
    const v = text.trim();
    if (prompt === 'birthday' && v) await send({ type: 'moment', moment: { type: 'birthday', name: v } });
    if (prompt === 'announce' && v) await send({ type: 'moment', moment: { type: 'announce', text: v } });
    if (prompt === 'mic') await send({ type: 'mic', active: true, speaker: v || null });
    setPrompt(null);
    setText('');
  };

  return (
    <div className="space-y-5">
      {error && <p className="rounded-xl bg-danger/15 px-4 py-3 text-sm text-danger">{error}</p>}

      <section>
        <h3 className="mb-2 text-xs font-bold tracking-widest text-ink-faint uppercase">Micro</h3>
        <button
          onClick={() => (state.mic.active ? void send({ type: 'mic', active: false }) : setPrompt('mic'))}
          className={cn(
            'w-full rounded-2xl py-5 text-xl font-bold transition',
            state.mic.active ? 'animate-pulse-soft bg-danger text-white' : 'bg-surface-2',
          )}
        >
          {state.mic.active ? `🎤 Micro ouvert${state.mic.speaker ? ` (${state.mic.speaker})` : ''} : remettre la musique` : '🎤 Prise de parole (baisse la musique)'}
        </button>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-bold tracking-widest text-ink-faint uppercase">Moments</h3>
        <div className="grid grid-cols-3 gap-2">
          <Tile emoji="🎂" label="Anniversaire" active={state.moment?.type === 'birthday'} onClick={() => setPrompt('birthday')} disabled={busy} />
          <Tile emoji="💞" label="Slows" active={state.moment?.type === 'slows'} onClick={() => void send({ type: 'moment', moment: { type: 'slows', count: 3 } })} disabled={busy} />
          <Tile emoji="🕺" label="Danse collective" active={state.moment?.type === 'collective'} onClick={() => void send({ type: 'moment', moment: { type: 'collective' } })} disabled={busy} />
          <Tile emoji="📸" label="Photo de groupe" active={state.moment?.type === 'photo'} onClick={() => void send({ type: 'moment', moment: { type: 'photo', seconds: 10 } })} disabled={busy} />
          <Tile emoji="🌙" label="Dernière danse" active={state.moment?.type === 'lastdance'} onClick={() => void send({ type: 'moment', moment: { type: 'lastdance' } })} disabled={busy} />
          <Tile emoji="📣" label="Annonce" active={state.moment?.type === 'announce'} onClick={() => setPrompt('announce')} disabled={busy} />
        </div>
        {state.moment && (
          <button onClick={() => void send({ type: 'momentClear' })} className="mt-2 w-full rounded-xl bg-surface py-2 text-sm text-ink-dim">
            Effacer le moment affiché
          </button>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-bold tracking-widest text-ink-faint uppercase">Jeu</h3>
        {bt ? (
          <button onClick={() => void send({ type: 'blindtestStop' })} className="w-full rounded-2xl bg-danger py-4 font-bold text-white">
            ⏹ Arrêter le blind test (manche {bt.round}/{bt.totalRounds})
          </button>
        ) : faces ? (
          <button onClick={() => void send({ type: 'facesStop' })} className="w-full rounded-2xl bg-danger py-4 font-bold text-white">
            ⏹ Arrêter Têtes Mêlées (manche {faces.round}/{faces.totalRounds})
          </button>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              {[3, 5, 10].map((n) => (
                <button key={n} onClick={() => void send({ type: 'blindtestStart', rounds: n })} disabled={busy} className="rounded-2xl bg-surface-2 py-4 font-semibold">
                  🎧 Blind test
                  <br />
                  <span className="text-sm text-ink-dim">{n} manches</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[3, 5, 10].map((n) => (
                <button
                  key={n}
                  onClick={() => void send({ type: 'facesStart', rounds: n })}
                  disabled={busy || state.faceCount < 2}
                  className="rounded-2xl bg-surface-2 py-4 font-semibold disabled:opacity-40"
                >
                  🤪 Têtes Mêlées
                  <br />
                  <span className="text-sm text-ink-dim">{n} manches</span>
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-ink-faint">
              📸 {state.faceCount} selfie{state.faceCount > 1 ? 's' : ''} pour Têtes Mêlées
              {state.faceCount < 2 ? ` (il en faut 2 : les invités l’ajoutent depuis leur téléphone)` : ''}
            </p>
          </div>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-xs font-bold tracking-widest text-ink-faint uppercase">Ambiance</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => void send({ type: 'phase', phaseId: null })}
            className={cn('rounded-full px-4 py-2 text-sm font-semibold', !state.phaseOverridden ? 'bg-gradient-neon' : 'bg-surface-2')}
          >
            ⏱ Auto
          </button>
          {state.phases.map((p) => (
            <button
              key={p.id}
              onClick={() => void send({ type: 'phase', phaseId: p.id })}
              className={cn('rounded-full px-4 py-2 text-sm font-semibold', state.phaseOverridden && state.phase.id === p.id ? 'bg-gradient-neon' : 'bg-surface-2')}
            >
              {p.emoji} {p.name}
            </button>
          ))}
        </div>
      </section>

      {showSkip && (
        <button onClick={() => void send({ type: 'skip' })} disabled={!!bt} className="w-full rounded-2xl bg-surface-2 py-4 font-semibold disabled:opacity-40">
          ⏭ Passer le titre en cours
        </button>
      )}

      {prompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setPrompt(null)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              void submitPrompt();
            }}
            className="animate-pop w-full max-w-md rounded-3xl bg-surface p-6"
          >
            <h3 className="font-display text-xl font-bold">
              {prompt === 'birthday' ? '🎂 Anniversaire de…' : prompt === 'announce' ? '📣 Message à afficher' : '🎤 Qui parle ? (facultatif)'}
            </h3>
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={prompt === 'announce' ? 120 : 40}
              placeholder={prompt === 'birthday' ? 'Mamie Josette' : prompt === 'announce' ? 'Le dessert est servi ! 🍰' : 'Les mariés'}
              className="mt-4 w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 text-lg outline-none focus:border-pink"
            />
            <div className="mt-4 flex gap-2">
              <button type="button" onClick={() => setPrompt(null)} className="flex-1 rounded-2xl bg-surface-2 py-3">
                Annuler
              </button>
              <button className="bg-gradient-neon flex-[2] rounded-2xl py-3 font-bold">C’est parti</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
